import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import {
  Box,
  Typography,
  Pagination,
  Dialog,
  Slide,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search, MapPin, X, Briefcase, Filter,
  Bookmark, Map, List, Save, Check, ArrowLeft, AlertCircle, EyeOff,
} from "lucide-react";
import { useSnackbar } from "notistack";
import axios from "axios";
import sh from "bundles/common/utils/sh";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import trackEvent from "bundles/common/utils/trackEvent";
import NewJobOfferCard from "bundles/Candidate/components/NewJobOfferCard";
import JobDetailPanel from "./JobDetailPanel";
import JobFilters from "./JobFilters";
import JobsMap from "./JobsMap";
import EmptyState from "./EmptyState";
import { geocodeCandidateAddress, searchPlaces } from "bundles/common/utils/locationCoordinates";
import {
  be_regions,
  lu_regions,
  fr_regions,
  companySubSectorMap,
} from "bundles/common/utils/constants";

const SlideLeftTransition = React.forwardRef(function SlideLeftTransition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const JobsTab = ({ preSelectedJob = null, onPreSelectedJobConsumed, filterRequest = null, onFilterRequestConsumed, onNavigate }) => {
  const intl = useIntl();
  const theme = useTheme();
  // noSsr: without it the first render reports desktop (false), and the very
  // first fetch — created in that render — auto-selects the first offer,
  // popping the full-screen detail open on phones.
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const currentCandidate = useSelector((state) => state.current_candidate);
  const [filtersSaved, setFiltersSaved] = useState(false);

  const [queryText, setQueryText] = useState(currentCandidate?.search_preferences?.query || "");
  const [debouncedQuery, setDebouncedQuery] = useState(currentCandidate?.search_preferences?.query || "");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [mobilePage, setMobilePage] = useState(1);
  const [fetchError, setFetchError] = useState(null);
  const [jobOffers, setJobOffers] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [mapSelectedJobId, setMapSelectedJobId] = useState(null);
  const [mapJobOffers, setMapJobOffers] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const detailPanelRef = useRef(null);
  const mapListRef = useRef(null);
  const mapCardRefs = useRef({});
  const showMapRef = useRef(false);
  const pendingJidRef = useRef(null);
  const pushedJidRef = useRef(false);
  const preselectedJobRef = useRef(false);
  const jobOffersRef = useRef([]);
  const lastSearchSignatureRef = useRef(null);
  // Bumped on every new search, so a "load more" started for a previous search is discarded.
  const searchGenerationRef = useRef(0);
  // Keeps the last opened job rendered during the dialog's exit animation.
  const lastMobileJobRef = useRef(null);

  // Write the selected job to the URL. "push" adds a history entry so the
  // phone back button closes the detail; "replace" keeps history untouched
  // (desktop master-detail selection, auto-selection).
  const syncJid = useCallback((jobId, mode = "replace") => {
    const url = new URL(window.location.href);
    if (jobId) url.searchParams.set("jid", jobId);
    else url.searchParams.delete("jid");
    if (mode === "push") window.history.pushState(null, "", url.toString());
    else window.history.replaceState(null, "", url.toString());
  }, []);

  const openJob = useCallback((offer) => {
    setSelectedJob(offer);
    if (isMobile) {
      syncJid(offer.id, "push");
      pushedJidRef.current = true;
    } else {
      syncJid(offer.id, "replace");
    }
    trackEvent("candidate_jobs", "detail_opened", { job_offer_id: offer.id });
  }, [isMobile, syncJid]);

  const closeJob = useCallback(() => {
    if (pushedJidRef.current) {
      // We pushed an entry when opening — go back so history stays in sync.
      pushedJidRef.current = false;
      window.history.back();
    } else {
      setSelectedJob(null);
      syncJid(null);
    }
  }, [syncJid]);

  // Leaving the tab from inside the detail (e.g. the "track application" link
  // in the post-apply snackbar). The mobile detail is a full-screen Dialog, so
  // it has to be closed explicitly or it covers the tab we just moved to.
  // Dropping jid from the current entry keeps the back button on the new tab
  // from re-opening the offer.
  const navigateAway = useCallback((tabId, job, opts) => {
    pushedJidRef.current = false;
    setSelectedJob(null);
    syncJid(null);
    onNavigate(tabId, job, opts);
  }, [onNavigate, syncJid]);

  useEffect(() => {
    const onPopState = () => {
      const jid = new URLSearchParams(window.location.search).get("jid");
      if (!jid) {
        pushedJidRef.current = false;
        setSelectedJob(null);
        return;
      }
      const found = jobOffersRef.current.find((o) => String(o.id) === String(jid));
      if (found) {
        setSelectedJob(found);
        pushedJidRef.current = true;
      } else {
        sh.get(`job_offer/${jid}`, {
          params: { authenticity_token: authenticityToken },
        }).then((res) => {
          const offer = res.data?.data;
          if (offer) {
            setSelectedJob(offer);
            pushedJidRef.current = true;
          }
        }).catch(() => {
          enqueueSnackbar(
            intl.formatMessage({ id: "candidate.jobs_tab.offer_unavailable", defaultMessage: "Cette offre n'est plus disponible." }),
            { variant: "info" }
          );
        });
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [authenticityToken]);

  useEffect(() => {
    if (preSelectedJob) {
      preselectedJobRef.current = true;
      openJob(preSelectedJob);
      // Cards passed from other tabs can be slim (no description) — hydrate.
      if (!preSelectedJob.description) {
        sh.get(`job_offer/${preSelectedJob.id}`, {
          params: { authenticity_token: authenticityToken },
        }).then((res) => {
          const full = res.data?.data;
          if (full) setSelectedJob(full);
        }).catch(() => {});
      }
      if (onPreSelectedJobConsumed) onPreSelectedJobConsumed();
    }
  }, [preSelectedJob]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jid = params.get("jid");
    if (!jid) return;
    pendingJidRef.current = jid;
    sh.get(`job_offer/${jid}`, {
      params: { authenticity_token: authenticityToken },
    }).then((res) => {
      const offer = res.data?.data;
      if (offer) setSelectedJob(offer);
    }).catch(() => {
      enqueueSnackbar(
        intl.formatMessage({ id: "candidate.jobs_tab.offer_unavailable", defaultMessage: "Cette offre n'est plus disponible." }),
        { variant: "info" }
      );
      syncJid(null);
    }).finally(() => {
      pendingJidRef.current = null;
    });
  }, [authenticityToken]);


  useEffect(() => {
    if (!selectedJob || isMobile) return;

    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      const el = detailPanelRef.current;
      if (!el) return;

      const TOP_OFFSET = 90;
      const BOTTOM_PADDING = 16;
      let currentTop = TOP_OFFSET;
      let lastScrollY = window.scrollY;

      el.style.position = "sticky";
      el.style.alignSelf = "flex-start";
      el.style.top = `${currentTop}px`;

      const onScroll = () => {
        const scrollY = window.scrollY;
        const delta = scrollY - lastScrollY;
        lastScrollY = scrollY;

        const panelHeight = el.offsetHeight;
        const vh = window.innerHeight;

        if (panelHeight <= vh - TOP_OFFSET - BOTTOM_PADDING) {
          currentTop = TOP_OFFSET;
        } else {
          const topMin = vh - panelHeight - BOTTOM_PADDING;
          currentTop = Math.max(topMin, Math.min(TOP_OFFSET, currentTop - delta));
        }

        el.style.top = `${currentTop}px`;
      };

      window.addEventListener("scroll", onScroll, { passive: true });
      el._cleanupSticky = () => {
        window.removeEventListener("scroll", onScroll);
        el.style.position = "";
        el.style.top = "";
        el.style.alignSelf = "";
      };
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      const el = detailPanelRef.current;
      if (el?._cleanupSticky) {
        el._cleanupSticky();
        delete el._cleanupSticky;
      }
    };
  }, [selectedJob?.id, isMobile]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(queryText), 350);
    return () => clearTimeout(timer);
  }, [queryText]);

  const savedPrefs = currentCandidate?.search_preferences || {};
  const [selectedRegions, setSelectedRegions] = useState(savedPrefs.regions || []);
  const [selectedLanguages, setSelectedLanguages] = useState(savedPrefs.languages || []);
  const [selectedIndustries, setSelectedIndustries] = useState(savedPrefs.industries || []);
  const [selectedSubcategories, setSelectedSubcategories] = useState(savedPrefs.subcategories || []);
  const [selectedJobStatuses, setSelectedJobStatuses] = useState(savedPrefs.job_statuses || []);
  const [selectedCompanySizes, setSelectedCompanySizes] = useState(savedPrefs.company_sizes || []);
  const [selectedBenefits, setSelectedBenefits] = useState(savedPrefs.benefits || []);
  const [onlyFavorites, setOnlyFavorites] = useState(
    new URLSearchParams(window.location.search).get("onlyFavorites") === "true" ||
    savedPrefs.only_favorites ||
    false
  );
  const [hideApplied, setHideApplied] = useState(savedPrefs.hide_applied || false);
  const [distanceFilter, setDistanceFilter] = useState(
    savedPrefs.distance_filter || null
  );
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    if (!filterRequest) return;
    if (filterRequest.onlyFavorites) setOnlyFavorites(true);
    if (onFilterRequestConsumed) onFilterRequestConsumed();
  }, [filterRequest]);

  const [regionSearch, setRegionSearch] = useState("");

  const availableRegions = useMemo(() => {
    const candidateCountry = currentCandidate?.country?.toLowerCase();
    const isFrance = candidateCountry && ["fr", "france"].includes(candidateCountry);
    if (isFrance) {
      const candidateRegion = currentCandidate?.region?.toLowerCase()?.replace(/-/g, "_");
      const extra = candidateRegion === "hauts_de_france" ? ["hainaut"] : [];
      return fr_regions.concat(extra);
    }
    return be_regions.concat(lu_regions).concat(fr_regions);
  }, [currentCandidate]);

  const filteredRegions = useMemo(() => {
    if (!regionSearch.trim()) return availableRegions;
    const q = regionSearch.toLowerCase();
    return availableRegions.filter((r) => {
      const label = intl.formatMessage({ id: `regions.${r}`, defaultMessage: r });
      return label.toLowerCase().includes(q) || r.toLowerCase().includes(q);
    });
  }, [availableRegions, regionSearch, intl]);

  useEffect(() => {
    if (!distanceFilter || distanceFilter.lat != null || !distanceFilter.label) return;
    let cancelled = false;
    searchPlaces(distanceFilter.label, intl.locale).then((places) => {
      if (cancelled) return;
      const place = places[0];
      setDistanceFilter((prev) => {
        if (!prev || prev.lat != null || prev.label !== distanceFilter.label) return prev;
        return place ? { ...prev, lat: place.lat, lng: place.lng } : null;
      });
    });
    return () => { cancelled = true; };
  }, [distanceFilter?.label, distanceFilter?.lat]);

  useEffect(() => {
    if (distanceFilter) return; // already set (from prefs or user)
    geocodeCandidateAddress(currentCandidate).then((result) => {
      if (result) {
        setDistanceFilter((prev) => prev || { lat: result.lat, lng: result.lng, km: 0, label: result.label });
      }
    });
  }, [currentCandidate]);

  // A distance of 0 km isn't sent as a filter, so geocoding the candidate's address on mount
  // (which sets a 0 km origin) must not count as a filter change and trigger a second search.
  const distanceKey = distanceFilter && distanceFilter.km > 0
    ? `${distanceFilter.lat},${distanceFilter.lng},${distanceFilter.km}`
    : null;

  const createFilters = useCallback(() => {
    const filters = {};
    if (selectedRegions.length > 0) filters.regions = selectedRegions;
    if (selectedLanguages.length > 0) filters.offer_language = selectedLanguages;
    if (selectedIndustries.length > 0) filters.company_industries = selectedIndustries;
    if (selectedSubcategories.length > 0) filters.company_sub_sectors = selectedSubcategories;
    if (selectedJobStatuses.length > 0) filters.job_statuses = selectedJobStatuses;
    if (selectedCompanySizes.length > 0) filters.company_size = selectedCompanySizes;
    if (selectedBenefits.length > 0) filters.offer_advantage_keys = selectedBenefits;
    if (onlyFavorites) filters.only_favorites = true;
    if (hideApplied) filters.exclude_applied = true;
    if (distanceFilter && distanceFilter.km > 0 && distanceFilter.lat != null) {
      filters.origin_lat = distanceFilter.lat;
      filters.origin_lng = distanceFilter.lng;
      filters.distance_km = distanceFilter.km;
    }
    return filters;
  }, [selectedRegions, selectedLanguages, selectedIndustries, selectedSubcategories, selectedJobStatuses, selectedCompanySizes, selectedBenefits, onlyFavorites, hideApplied, distanceKey]);

  const fetchJobOffers = useCallback((opts = {}) => {
    const p = opts.page || page;
    const q = opts.query !== undefined ? opts.query : debouncedQuery;
    const f = opts.filters || createFilters();

    searchGenerationRef.current += 1;
    setLoadingMore(false);
    setLoading(true);
    setFetchError(null);
    sh.get("search/job_offers", {
      params: {
        authenticity_token: authenticityToken,
        query: q,
        page: p,
        filters: JSON.stringify(f),
      },
    })
      .then((res) => {
        const data = res.data?.data || [];
        setJobOffers(data);
        jobOffersRef.current = data;
        setMobilePage(p);
        setPageCount(res.data?.pagy?.pages || 1);
        setCount(res.data?.pagy?.count || 0);

        if (p === 1) {
          const signature = JSON.stringify({ q: q || "", f });
          if (lastSearchSignatureRef.current !== null && lastSearchSignatureRef.current !== signature) {
            trackEvent("candidate_jobs", "searched", {
              query: q || "",
              filters: { ...f, origin_lat: undefined, origin_lng: undefined },
              result_count: res.data?.pagy?.count || 0,
            });
          }
          lastSearchSignatureRef.current = signature;
        }

        if (data.length > 0 && !isMobile && !showMapRef.current && !pendingJidRef.current && !preselectedJobRef.current) {
          setSelectedJob(data[0]);
          syncJid(data[0].id, "replace");
        } else if (showMapRef.current || pendingJidRef.current || preselectedJobRef.current || (isMobile && pushedJidRef.current)) {
          preselectedJobRef.current = false;
        } else {
          setSelectedJob(null);
          syncJid(null);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        console.error(err);
        setFetchError(err.response || err.code === "ECONNABORTED" ? "server" : "network");
        setLoading(false);
      });
  }, [page, debouncedQuery, createFilters, authenticityToken, isMobile]);

  useEffect(() => {
    fetchJobOffers({ page, query: debouncedQuery, filters: createFilters() });
  }, [page, debouncedQuery, selectedRegions, selectedLanguages, selectedIndustries, selectedSubcategories, selectedJobStatuses, selectedCompanySizes, selectedBenefits, onlyFavorites, hideApplied, distanceKey]);

  const fetchMapJobOffers = useCallback(() => {
    setMapLoading(true);
    sh.get("search/map_job_offers", {
      params: {
        authenticity_token: authenticityToken,
        query: debouncedQuery,
        filters: JSON.stringify(createFilters()),
      },
    })
      .then((res) => {
        setMapJobOffers(res.data?.data || []);
      })
      .catch((err) => {
        console.error("Map offers fetch failed:", err);
      })
      .finally(() => {
        setMapLoading(false);
      });
  }, [debouncedQuery, createFilters, authenticityToken]);

  useEffect(() => {
    if (showMap) fetchMapJobOffers();
  }, [showMap, fetchMapJobOffers]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, selectedRegions, selectedLanguages, selectedIndustries, selectedSubcategories, selectedJobStatuses, selectedCompanySizes, selectedBenefits, onlyFavorites, hideApplied, distanceKey]);

  const activeFilterCount = [
    selectedRegions.length > 0 ? 1 : 0,
    selectedLanguages.length > 0 ? 1 : 0,
    selectedIndustries.length > 0 ? 1 : 0,
    selectedJobStatuses.length > 0 ? 1 : 0,
    selectedCompanySizes.length > 0 ? 1 : 0,
    selectedBenefits.length > 0 ? 1 : 0,
    distanceFilter && distanceFilter.km > 0 ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const clearFilters = () => {
    setSelectedRegions([]);
    setSelectedLanguages([]);
    setSelectedIndustries([]);
    setSelectedSubcategories([]);
    setSelectedJobStatuses([]);
    setSelectedCompanySizes([]);
    setSelectedBenefits([]);
    setRegionSearch("");
    setOnlyFavorites(false);
    setHideApplied(false);
    setDistanceFilter((prev) => prev ? { ...prev, km: 0 } : prev);
  };

  const toggleRegion = (r) => {
    setSelectedRegions((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]
    );
  };

  const toggleLanguage = (l) => {
    setSelectedLanguages((prev) =>
      prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]
    );
  };

  const toggleIndustry = (ind) => {
    setSelectedIndustries((prev) => {
      if (prev.includes(ind)) {
        const subs = companySubSectorMap[ind] || [];
        if (subs.length > 0) {
          setSelectedSubcategories((prevSubs) => prevSubs.filter((s) => !subs.includes(s)));
        }
        return prev.filter((x) => x !== ind);
      }
      return [...prev, ind];
    });
  };

  const toggleJobStatus = (s) => {
    setSelectedJobStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const toggleCompanySize = (size) => {
    setSelectedCompanySizes((prev) =>
      prev.includes(size) ? prev.filter((x) => x !== size) : [...prev, size]
    );
  };

  const toggleBenefit = (key) => {
    setSelectedBenefits((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const handleJobClick = (offer) => {
    openJob(offer);
  };

  // Mobile appends the next page under the list ("load more") instead of the
  // desktop pagination control.
  const handleLoadMore = () => {
    if (loading || loadingMore) return;
    const next = mobilePage + 1;
    const generation = searchGenerationRef.current;
    setLoadingMore(true);
    sh.get("search/job_offers", {
      params: {
        authenticity_token: authenticityToken,
        query: debouncedQuery,
        page: next,
        filters: JSON.stringify(createFilters()),
      },
      // Own cancel key, so loading more never cancels a new search (same URL).
      cancelKey: "search/job_offers:load_more",
    })
      .then((res) => {
        // The filters or query changed while this page was loading: it belongs to the old list.
        if (generation !== searchGenerationRef.current) return;
        const data = res.data?.data || [];
        setJobOffers((prev) => {
          const merged = [...prev, ...data];
          jobOffersRef.current = merged;
          return merged;
        });
        setMobilePage(next);
        setPageCount(res.data?.pagy?.pages || 1);
      })
      .catch((err) => {
        if (axios.isCancel(err) || generation !== searchGenerationRef.current) return;
        enqueueSnackbar(
          intl.formatMessage({ id: "candidate.dashboard.action_failed", defaultMessage: "Une erreur est survenue. Réessayez." }),
          { variant: "error" }
        );
      })
      .finally(() => {
        if (generation === searchGenerationRef.current) setLoadingMore(false);
      });
  };

  const isApplied = useCallback((offer) => Boolean(
    currentCandidate?.id &&
    offer?.job_applications?.some((a) => String(a.candidate_id) === String(currentCandidate.id))
  ), [currentCandidate?.id]);

  const updateJobOffer = (updatedOffer) => {
    const withUpdate = (list) => {
      const idx = list.findIndex((o) => o.id === updatedOffer.id);
      if (idx < 0) return list;
      const next = [...list];
      next[idx] = updatedOffer;
      return next;
    };
    // The ref feeds the popstate handler, which re-selects the offer by jid.
    // Without this it hands back a pre-application copy and the detail reverts
    // to its "not applied yet" state.
    jobOffersRef.current = withUpdate(jobOffersRef.current);
    setJobOffers(withUpdate);
    if (selectedJob?.id === updatedOffer.id) {
      setSelectedJob(updatedOffer);
    }
  };

  const saveDefaultFilters = useCallback(() => {
    const prefs = {
      query: queryText,
      regions: selectedRegions,
      languages: selectedLanguages,
      industries: selectedIndustries,
      subcategories: selectedSubcategories,
      job_statuses: selectedJobStatuses,
      company_sizes: selectedCompanySizes,
      benefits: selectedBenefits,
      only_favorites: onlyFavorites,
      hide_applied: hideApplied,
      distance_filter: distanceFilter ? { label: distanceFilter.label, km: distanceFilter.km } : null,
    };
    sh.put("candidate", {
      authenticity_token: authenticityToken,
      candidate: { id: currentCandidate.id, search_preferences: prefs },
    }).then((res) => {
      dispatch({ type: "UPDATE_CURRENT_CANDIDATE", payload: res.data });
      setFiltersSaved(true);
      setTimeout(() => setFiltersSaved(false), 2000);
    }).catch(() => {
      enqueueSnackbar(
        intl.formatMessage({ id: "candidate.dashboard.action_failed", defaultMessage: "Une erreur est survenue. Réessayez." }),
        { variant: "error" }
      );
    });
  }, [queryText, selectedRegions, selectedLanguages, selectedIndustries, selectedSubcategories, selectedJobStatuses, selectedCompanySizes, selectedBenefits, onlyFavorites, hideApplied, distanceFilter, authenticityToken, currentCandidate, dispatch]);

  if (selectedJob) lastMobileJobRef.current = selectedJob;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>

      {/* Search bar row */}
      <Box sx={{ display: "flex", gap: "8px", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
        {/* Query search */}
        <Box sx={{
          flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "12px",
          bgcolor: "hsl(0, 0%, 100%)", border: "1px solid hsl(160, 10%, 90%)",
          borderRadius: "16px", px: { xs: "12px", sm: "16px" }, py: "12px",
          "&:focus-within": { borderColor: "hsla(153, 68%, 25%, 0.3)" },
          transition: "border-color 0.15s",
        }}>
          <Search size={16} color="hsl(178, 10%, 45%)" />
          <Box
            component="input"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            placeholder={intl.formatMessage({ id: "candidate.jobs_tab.search_placeholder" })}
            sx={{
              // width/minWidth beat the input's intrinsic ~170px minimum,
              // which pushed the last toolbar button onto a second line.
              flex: 1, width: "100%", minWidth: 0,
              border: "none", outline: "none", bgcolor: "transparent",
              fontSize: "14px", fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: "hsl(178, 93%, 10%)",
              "&::placeholder": { color: "hsl(178, 10%, 45%)", opacity: 0.5 },
            }}
          />
          {queryText && (
            <Box component="button" onClick={() => setQueryText("")} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex" }}>
              <X size={14} color="hsl(178, 10%, 45%)" />
            </Box>
          )}
        </Box>

        {/* Region search bar — full-width second row on mobile */}
        {(
          <Box sx={{ position: "relative", width: { xs: "100%", sm: "240px" }, order: { xs: 10, sm: 0 } }}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: "12px", width: "100%",
              bgcolor: "hsl(0, 0%, 100%)", border: "1px solid hsl(160, 10%, 90%)",
              borderRadius: "16px", px: "16px", py: "12px",
              "&:focus-within": { borderColor: "hsla(153, 68%, 25%, 0.3)" },
              transition: "border-color 0.15s",
            }}>
              <MapPin size={16} color="hsl(178, 10%, 45%)" />
              <Box
                component="input"
                value={regionSearch}
                onChange={(e) => setRegionSearch(e.target.value)}
                placeholder={intl.formatMessage({ id: "candidate.jobs_tab.region_placeholder" })}
                sx={{
                  flex: 1, border: "none", outline: "none", bgcolor: "transparent",
                  fontSize: "14px", fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(178, 93%, 10%)", minWidth: 0,
                  "&::placeholder": { color: "hsl(178, 10%, 45%)", opacity: 0.5 },
                }}
              />
              {regionSearch && (
                <Box component="button" onClick={() => setRegionSearch("")} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex" }}>
                  <X size={14} color="hsl(178, 10%, 45%)" />
                </Box>
              )}
            </Box>

            {selectedRegions.length > 0 && (
              <Box sx={{ display: { xs: "flex", sm: "none" }, flexWrap: "wrap", gap: "6px", mt: "8px" }}>
                {selectedRegions.map((r) => (
                  <Box
                    key={r}
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: "6px",
                      px: "10px", py: "5px", borderRadius: "9999px",
                      bgcolor: "hsla(153, 68%, 25%, 0.1)", color: "hsl(153, 68%, 25%)",
                      fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                    }}
                  >
                    {intl.formatMessage({ id: `regions.${r}`, defaultMessage: r })}
                    <Box
                      component="button"
                      type="button"
                      aria-label={intl.formatMessage({ id: "candidate.jobs_tab.remove_region" })}
                      onClick={() => toggleRegion(r)}
                      sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit" }}
                    >
                      <X size={11} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {regionSearch && filteredRegions.length > 0 && (
              <Box sx={{
                position: "absolute",
                top: "calc(100% + 8px)",
                left: 0,
                right: 0,
                zIndex: 20,
                bgcolor: "hsl(0, 0%, 100%)",
                border: "1px solid hsl(160, 10%, 90%)",
                borderRadius: "12px",
                p: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                boxShadow: "0 4px 12px hsla(178, 93%, 10%, 0.08)",
                maxHeight: "240px",
                overflowY: "auto",
              }}>
                {[
                  { key: "belgium", regions: be_regions },
                  { key: "luxembourg", regions: lu_regions },
                  { key: "france", regions: fr_regions },
                ]
                  .map((g) => ({ ...g, items: filteredRegions.filter((r) => g.regions.includes(r)) }))
                  .filter((g) => g.items.length > 0)
                  .map((group) => (
                    <Box key={group.key} sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <Typography sx={{
                        px: "10px", pt: "6px", pb: "2px",
                        fontSize: "10px", letterSpacing: "0.04em", textTransform: "uppercase",
                        fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                        color: "hsla(178, 10%, 45%, 0.7)",
                      }}>
                        {intl.formatMessage({ id: `countries.${group.key}`, defaultMessage: group.key })}
                      </Typography>
                      {group.items.map((r) => {
                        const active = selectedRegions.includes(r);
                        return (
                          <Box
                            key={r}
                            component="button"
                            onClick={() => { toggleRegion(r); setRegionSearch(""); }}
                            sx={{
                              px: "12px", py: "8px", borderRadius: "10px", fontSize: "12px",
                              cursor: "pointer", border: "none", transition: "all 0.15s",
                              textAlign: "left",
                              fontFamily: active ? "Segoe UI Bold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui",
                              bgcolor: active ? "hsl(178, 93%, 10%)" : "transparent",
                              color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 10%, 45%)",
                              "&:hover": {
                                bgcolor: active ? "hsl(178, 93%, 10%)" : "hsla(160, 10%, 96%, 0.5)",
                                color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 93%, 10%)",
                              },
                            }}
                          >
                            {intl.formatMessage({ id: `regions.${r}`, defaultMessage: r })}
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
              </Box>
            )}
          </Box>
        )}

        {/* Map toggle */}
        <Box
          component="button"
          onClick={() => { const next = !showMap; showMapRef.current = next; if (next) { setSelectedJob(null); trackEvent("candidate_jobs", "map_used"); } setShowMap(next); }}
          sx={{
            display: "flex", alignItems: "center", gap: "6px",
            px: { xs: "12px", sm: "16px" }, py: { xs: "12px", sm: 0 }, borderRadius: "16px", border: "1px solid",
            cursor: "pointer", fontSize: "12px",
            fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            bgcolor: "hsl(0, 0%, 100%)",
            borderColor: "hsl(160, 10%, 90%)",
            color: "hsl(178, 10%, 45%)",
            "&:hover": { color: "hsl(178, 93%, 10%)" },
          }}
        >
          {showMap ? <List size={14} /> : <Map size={14} />}
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            {showMap ? intl.formatMessage({ id: "candidate.jobs_tab.view_list" }) : intl.formatMessage({ id: "candidate.jobs_tab.view_map" })}
          </Box>
        </Box>

        {/* Filter button */}
        <Box
          component="button"
          onClick={() => { setFiltersOpen(true); trackEvent("candidate_jobs", "filters_opened"); }}
          sx={{
            position: "relative", display: "flex", alignItems: "center", gap: "6px",
            px: { xs: "12px", sm: "16px" }, py: { xs: "12px", sm: 0 }, borderRadius: "16px", border: "1px solid", cursor: "pointer",
            fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            bgcolor: activeFilterCount > 0 ? "hsla(153, 68%, 25%, 0.1)" : "hsl(0, 0%, 100%)",
            borderColor: activeFilterCount > 0 ? "hsla(153, 68%, 25%, 0.3)" : "hsl(160, 10%, 90%)",
            color: activeFilterCount > 0 ? "hsl(153, 68%, 25%)" : "hsl(178, 10%, 45%)",
            "&:hover": { color: "hsl(178, 93%, 10%)" },
          }}
        >
          <Filter size={14} />
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{intl.formatMessage({ id: "candidate.jobs_tab.filters" })}</Box>
          {activeFilterCount > 0 && (
            <Box
              component="span"
              sx={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: "16px", height: "16px", borderRadius: "9999px",
                bgcolor: "hsl(153, 68%, 25%)", color: "hsl(0, 0%, 100%)",
                fontSize: "9px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              }}
            >
              {activeFilterCount}
            </Box>
          )}
        </Box>

        {/* Save default filters */}
        <Box
          component="button"
          onClick={saveDefaultFilters}
          sx={{
            display: "flex", alignItems: "center", gap: "6px",
            px: { xs: "12px", sm: "16px" }, py: { xs: "12px", sm: 0 }, borderRadius: "16px", border: "1px solid",
            cursor: "pointer", fontSize: "12px",
            fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            bgcolor: filtersSaved ? "hsla(153, 68%, 25%, 0.1)" : "hsl(0, 0%, 100%)",
            borderColor: filtersSaved ? "hsla(153, 68%, 25%, 0.3)" : "hsl(160, 10%, 90%)",
            color: filtersSaved ? "hsl(153, 68%, 25%)" : "hsl(178, 10%, 45%)",
            "&:hover": { color: "hsl(178, 93%, 10%)" },
            transition: "all 0.2s",
          }}
        >
          {filtersSaved ? <Check size={14} /> : <Save size={14} />}
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            {filtersSaved ? intl.formatMessage({ id: "candidate.jobs_tab.filters_saved" }) : intl.formatMessage({ id: "candidate.jobs_tab.save_filters" })}
          </Box>
        </Box>
      </Box>

      {/* Active filter chips */}
      {(activeFilterCount > 0 || selectedRegions.length > 0 || selectedSubcategories.length > 0) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          {selectedRegions.map((r) => (
            <Box
              key={r}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                px: "12px", py: "6px", borderRadius: "9999px",
                bgcolor: "hsl(178, 93%, 10%)", color: "hsl(0, 0%, 100%)",
                fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              }}
            >
              {intl.formatMessage({ id: `regions.${r}`, defaultMessage: r })}
              <Box
                component="button"
                onClick={() => toggleRegion(r)}
                sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit", "&:hover": { opacity: 0.7 } }}
              >
                <X size={11} />
              </Box>
            </Box>
          ))}
          {selectedLanguages.map((l) => (
            <Box
              key={l}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                px: "12px", py: "6px", borderRadius: "9999px",
                bgcolor: "hsl(178, 93%, 10%)", color: "hsl(0, 0%, 100%)",
                fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              }}
            >
              {intl.formatMessage({ id: `languages.${l}`, defaultMessage: l })}
              <Box
                component="button"
                onClick={() => toggleLanguage(l)}
                sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit", "&:hover": { opacity: 0.7 } }}
              >
                <X size={11} />
              </Box>
            </Box>
          ))}
          {selectedIndustries.map((ind) => (
            <Box
              key={ind}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                px: "12px", py: "6px", borderRadius: "9999px",
                bgcolor: "hsl(178, 93%, 10%)", color: "hsl(0, 0%, 100%)",
                fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              }}
            >
              {intl.formatMessage({ id: `company.company_industry.${ind}`, defaultMessage: ind })}
              <Box
                component="button"
                onClick={() => toggleIndustry(ind)}
                sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit", "&:hover": { opacity: 0.7 } }}
              >
                <X size={11} />
              </Box>
            </Box>
          ))}
          {selectedSubcategories.map((sub) => (
            <Box
              key={sub}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                px: "12px", py: "6px", borderRadius: "9999px",
                bgcolor: "hsl(178, 93%, 10%)", color: "hsl(0, 0%, 100%)",
                fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              }}
            >
              {intl.formatMessage({ id: `company.company_sub_sector.${sub}`, defaultMessage: sub.replace(/_/g, " ") })}
              <Box
                component="button"
                onClick={() => setSelectedSubcategories((prev) => prev.filter((x) => x !== sub))}
                sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit", "&:hover": { opacity: 0.7 } }}
              >
                <X size={11} />
              </Box>
            </Box>
          ))}
          {selectedJobStatuses.map((s) => (
            <Box
              key={s}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                px: "12px", py: "6px", borderRadius: "9999px",
                bgcolor: "hsl(178, 93%, 10%)", color: "hsl(0, 0%, 100%)",
                fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              }}
            >
              {intl.formatMessage({ id: `candidate.status_map.${s}`, defaultMessage: s })}
              <Box
                component="button"
                onClick={() => toggleJobStatus(s)}
                sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit", "&:hover": { opacity: 0.7 } }}
              >
                <X size={11} />
              </Box>
            </Box>
          ))}
          {selectedCompanySizes.map((size) => (
            <Box
              key={size}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                px: "12px", py: "6px", borderRadius: "9999px",
                bgcolor: "hsl(178, 93%, 10%)", color: "hsl(0, 0%, 100%)",
                fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              }}
            >
              {intl.formatMessage({ id: `company.company_size.options.${size}`, defaultMessage: size.replace(/_/g, " ") })}
              <Box
                component="button"
                onClick={() => toggleCompanySize(size)}
                sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit", "&:hover": { opacity: 0.7 } }}
              >
                <X size={11} />
              </Box>
            </Box>
          ))}
          {selectedBenefits.map((key) => {
            return (
              <Box
                key={key}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  px: "12px", py: "6px", borderRadius: "9999px",
                  bgcolor: "hsl(178, 93%, 10%)", color: "hsl(0, 0%, 100%)",
                  fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                }}
              >
                {intl.formatMessage({ id: `company_view.profile.advantages.items.${key}`, defaultMessage: key.replace(/_/g, " ") })}
                <Box
                  component="button"
                  onClick={() => toggleBenefit(key)}
                  sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit", "&:hover": { opacity: 0.7 } }}
                >
                  <X size={11} />
                </Box>
              </Box>
            );
          })}
          {distanceFilter && distanceFilter.km > 0 && (
            <Box
              sx={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                px: "12px", py: "6px", borderRadius: "9999px",
                bgcolor: "hsl(178, 93%, 10%)", color: "hsl(0, 0%, 100%)",
                fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              }}
            >
              {`${distanceFilter.km} km — ${distanceFilter.label || intl.formatMessage({ id: "candidate.jobs_tab.my_position" })}`}
              <Box
                component="button"
                onClick={() => setDistanceFilter((prev) => prev ? { ...prev, km: 0 } : prev)}
                sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit", "&:hover": { opacity: 0.7 } }}
              >
                <X size={11} />
              </Box>
            </Box>
          )}
          <Box
            component="button"
            onClick={clearFilters}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              px: "12px", py: "6px", borderRadius: "9999px",
              border: "1px solid hsla(160, 10%, 90%, 0.9)",
              bgcolor: "transparent", color: "hsl(178, 10%, 45%)",
              fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              cursor: "pointer", transition: "all 0.15s",
              "&:hover": { color: "hsl(0, 65%, 51%)", borderColor: "hsla(0, 65%, 51%, 0.4)" },
            }}
          >
            <X size={11} />
            {intl.formatMessage({ id: "candidate.jobs_tab.reset" })}
          </Box>
        </Box>
      )}

      {/* Count + saved toggle */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
          {intl.formatMessage({ id: "candidate.jobs_tab.results_count" }, { count })}
        </Typography>
        <Box
          component="button"
          onClick={() => setOnlyFavorites(!onlyFavorites)}
          sx={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            px: "12px", py: "4px", borderRadius: "9999px", cursor: "pointer",
            fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            border: "1px solid",
            bgcolor: onlyFavorites ? "hsla(153, 68%, 25%, 0.1)" : "hsla(160, 10%, 96%, 0.5)",
            borderColor: onlyFavorites ? "hsla(153, 68%, 25%, 0.3)" : "hsl(160, 10%, 90%)",
            color: onlyFavorites ? "hsl(153, 68%, 25%)" : "hsl(178, 10%, 45%)",
            "&:hover": { color: "hsl(178, 93%, 10%)", bgcolor: "hsla(160, 10%, 96%, 0.8)" },
          }}
        >
          <Bookmark size={12} style={onlyFavorites ? { fill: "currentColor" } : {}} />
          {intl.formatMessage({ id: "candidate.jobs_tab.only_saved" })}
        </Box>
        <Box
          component="button"
          onClick={() => setHideApplied(!hideApplied)}
          aria-pressed={hideApplied}
          sx={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            px: "12px", py: "4px", borderRadius: "9999px", cursor: "pointer",
            fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            border: "1px solid",
            bgcolor: hideApplied ? "hsla(153, 68%, 25%, 0.1)" : "hsla(160, 10%, 96%, 0.5)",
            borderColor: hideApplied ? "hsla(153, 68%, 25%, 0.3)" : "hsl(160, 10%, 90%)",
            color: hideApplied ? "hsl(153, 68%, 25%)" : "hsl(178, 10%, 45%)",
            "&:hover": { color: "hsl(178, 93%, 10%)", bgcolor: "hsla(160, 10%, 96%, 0.8)" },
          }}
        >
          <EyeOff size={12} />
          {intl.formatMessage({ id: "candidate.jobs_tab.hide_applied", defaultMessage: "Masquer les offres postulées" })}
        </Box>
      </Box>

      {/* Loading state: the branded loader, centred where the results will appear */}
      {(loading || (showMap && mapLoading)) && jobOffers.length === 0 && (
        <SquarehubLoader size={56} centered minHeight="50vh" />
      )}

      {/* Fetch error: explicit state with retry instead of a fake empty list */}
      {!loading && fetchError && (
        <EmptyState
          icon={AlertCircle}
          py="64px"
          title={intl.formatMessage({ id: "candidate.dashboard.load_error_title", defaultMessage: "Impossible de charger les données" })}
          subtitle={intl.formatMessage(
            fetchError === "server"
              ? { id: "candidate.dashboard.load_error_hint_server", defaultMessage: "Nos serveurs mettent trop de temps à répondre. Réessayez dans un instant." }
              : { id: "candidate.dashboard.load_error_hint", defaultMessage: "Vérifiez votre connexion internet." }
          )}
          ctaLabel={intl.formatMessage({ id: "candidate.dashboard.retry", defaultMessage: "Réessayer" })}
          onCta={() => fetchJobOffers()}
        />
      )}

      {/* Map view */}
      {showMap && !fetchError && !((loading || mapLoading) && jobOffers.length === 0) && (
        <Box sx={{
          display: "flex", gap: "16px", alignItems: "flex-start",
          flexDirection: { xs: "column", md: "row" },
        }}>
          <Box sx={{
            width: { xs: "100%", md: "400px" },
            flexShrink: 0,
            // On mobile the map is the whole point of this view — the cards are
            // still one tap away via the list toggle.
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            gap: "8px",
            maxHeight: { md: "calc(100vh - 6rem)" },
            overflowY: { md: "auto" },
          }} ref={mapListRef}>
            {jobOffers.map((offer, i) => (
              <motion.div
                key={offer.id}
                ref={(el) => { mapCardRefs.current[offer.id] = el; }}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03, duration: 0.2 }}
              >
                <Box
                  sx={{
                    maxWidth: "585px",
                    borderRadius: "12px",
                    border: mapSelectedJobId === offer.id
                      ? "2px solid hsla(153, 68%, 25%, 0.5)"
                      : "2px solid transparent",
                    transition: "border-color 0.2s",
                    "&:hover": { borderColor: "hsla(153, 68%, 25%, 0.3)" },
                  }}
                >
                  <NewJobOfferCard
                    offer={offer}
                    onClick={() => {
                      handleJobClick(offer);
                      setMapSelectedJobId(offer.id);
                    }}
                    hideDescription
                    applied={isApplied(offer)}
                  />
                </Box>
              </motion.div>
            ))}
          </Box>
          <Box sx={{
            flex: { xs: "none", md: 1 }, minWidth: 0, display: "flex",
            // Map sits above the list on mobile, beside it from md up.
            order: { xs: -1, md: 0 },
            width: { xs: "100%", md: "auto" },
            position: { xs: "static", md: "sticky" }, top: { md: "80px" },
            height: { xs: "calc(100vh - 13rem)", md: "calc(100vh - 6rem)" },
            borderRadius: "16px",
            overflow: "hidden",
          }}>
            {selectedJob && !isMobile ? (
              <Box sx={{ width: "100%", height: "100%", overflowY: "auto", position: "relative" }}>
                <Box
                  component="button"
                  onClick={closeJob}
                  sx={{
                    position: "absolute", top: "12px", right: "12px", zIndex: 10,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: "28px", height: "28px", borderRadius: "9999px",
                    border: "none", bgcolor: "hsl(0, 0%, 100%)", cursor: "pointer",
                    color: "hsl(178, 10%, 45%)",
                    "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.8)", color: "hsl(178, 93%, 10%)" },
                  }}
                >
                  <X size={14} />
                </Box>
                <JobDetailPanel
                  jobOffer={selectedJob}
                  updateJobOffer={updateJobOffer}
                  onNavigate={navigateAway}
                />
              </Box>
            ) : (
              <JobsMap
                jobOffers={mapJobOffers}
                selectedJobId={mapSelectedJobId}
                onSelectJob={(offer) => {
                  setMapSelectedJobId(offer.id);
                  const cardEl = mapCardRefs.current[offer.id];
                  if (cardEl) {
                    cardEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
                    const fullOffer = jobOffers.find((o) => o.id === offer.id);
                    if (fullOffer) handleJobClick(fullOffer);
                  } else {
                    sh.get(`job_offer/${offer.id}`, {
                      params: { authenticity_token: authenticityToken },
                    }).then((res) => {
                      const full = res.data?.data;
                      if (full) openJob(full);
                    }).catch(() => {
                      openJob(offer);
                    });
                  }
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* List layout: cards left + detail right */}
      {!showMap && !fetchError && !(loading && jobOffers.length === 0) && (
        <>
          <Box sx={{
            display: "flex", gap: "16px", alignItems: "flex-start",
            opacity: loading ? 0.55 : 1, transition: "opacity 0.15s",
            pointerEvents: loading ? "none" : "auto",
          }}>
            {/* Left: job list */}
            <Box sx={{
              width: selectedJob && !isMobile ? "400px" : "100%",
              flexShrink: 0,
              display: "flex", flexDirection: "column", gap: "8px",
              transition: "width 0.3s",
            }}>
              {jobOffers.map((offer, i) => (
                <motion.div
                  key={offer.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03, duration: 0.2 }}
                >
                  <Box
                    sx={{
                      maxWidth: "585px",
                      borderRadius: "12px",
                      border: selectedJob?.id === offer.id ? "2px solid hsla(153, 68%, 25%, 0.5)" : "2px solid transparent",
                      transition: "border-color 0.2s",
                      "&:hover": { borderColor: "hsla(153, 68%, 25%, 0.3)" },
                    }}
                  >
                    <NewJobOfferCard
                      offer={offer}
                      onClick={() => handleJobClick(offer)}
                      hideDescription
                      applied={isApplied(offer)}
                    />
                  </Box>
                </motion.div>
              ))}

              {jobOffers.length === 0 && !loading && (
                <EmptyState
                  icon={Briefcase}
                  py="64px"
                  title={intl.formatMessage({ id: "candidate.jobs_tab.no_offers_found" })}
                  subtitle={intl.formatMessage({ id: "candidate.jobs_tab.adjust_filters" })}
                  ctaLabel={activeFilterCount > 0 ? intl.formatMessage({ id: "candidate.jobs_tab.reset" }) : undefined}
                  onCta={activeFilterCount > 0 ? clearFilters : undefined}
                />
              )}
            </Box>

            {/* Right: detail panel (desktop only) */}
            {selectedJob && !isMobile && (
              <Box ref={detailPanelRef} sx={{ flex: 1, minWidth: 0, overflow: "auto" }}>
                <JobDetailPanel
                  jobOffer={selectedJob}
                  updateJobOffer={updateJobOffer}
                  onNavigate={navigateAway}
                />
              </Box>
            )}
          </Box>

          {/* Mobile: load more appends to the list */}
          {isMobile && mobilePage < pageCount && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: "8px" }}>
              <Box
                component="button"
                onClick={handleLoadMore}
                disabled={loadingMore}
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  width: "100%", py: "14px", borderRadius: "12px",
                  border: "1px solid hsl(160, 10%, 90%)", bgcolor: "hsl(0, 0%, 100%)",
                  color: "hsl(178, 93%, 10%)", fontSize: "14px",
                  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                  cursor: loadingMore ? "wait" : "pointer",
                  opacity: loadingMore ? 0.6 : 1,
                }}
              >
                {intl.formatMessage({ id: "candidate.jobs_tab.load_more", defaultMessage: "Voir plus d'offres" })}
              </Box>
            </Box>
          )}

          {/* Pagination — outside the flex row so sticky panel doesn't extend past cards */}
          {!isMobile && pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: "16px", width: selectedJob && !isMobile ? "400px" : "100%" }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, v) => setPage(v)}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    color: "hsl(178, 10%, 45%)",
                    "&.Mui-selected": {
                      bgcolor: "hsl(178, 93%, 10%)",
                      color: "hsl(0, 0%, 100%)",
                      "&:hover": { bgcolor: "hsl(178, 93%, 15%)" },
                    },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Filter Dialog */}
      <Dialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "16px",
            bgcolor: "hsl(0, 0%, 100%)",
            overflow: "hidden",
            maxHeight: isMobile ? "100%" : "85vh",
          },
        }}
      >
        <JobFilters
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          regionSearch={regionSearch}
          setRegionSearch={setRegionSearch}
          filteredRegions={filteredRegions}
          selectedRegions={selectedRegions}
          toggleRegion={toggleRegion}
          selectedLanguages={selectedLanguages}
          toggleLanguage={toggleLanguage}
          selectedIndustries={selectedIndustries}
          toggleIndustry={toggleIndustry}
          selectedSubcategories={selectedSubcategories}
          setSelectedSubcategories={setSelectedSubcategories}
          selectedJobStatuses={selectedJobStatuses}
          toggleJobStatus={toggleJobStatus}
          selectedCompanySizes={selectedCompanySizes}
          toggleCompanySize={toggleCompanySize}
          selectedBenefits={selectedBenefits}
          toggleBenefit={toggleBenefit}
          clearFilters={clearFilters}
          count={count}
          isMobile={isMobile}
          distanceFilter={distanceFilter}
          setDistanceFilter={setDistanceFilter}
        />
      </Dialog>

      {/* Mobile: full-screen job detail (slide-in page) */}
      {isMobile && (
        <Dialog
          fullScreen
          open={Boolean(selectedJob)}
          onClose={closeJob}
          TransitionComponent={SlideLeftTransition}
          PaperProps={{
            sx: {
              bgcolor: "hsla(160, 10%, 96%, 1)",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {(() => {
            const job = selectedJob || lastMobileJobRef.current;
            if (!job) return null;
            return (
              <>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: "8px",
                  px: "8px", py: "6px", flexShrink: 0,
                  bgcolor: "hsl(0, 0%, 100%)",
                  borderBottom: "1px solid hsl(160, 10%, 90%)",
                  pt: "calc(6px + env(safe-area-inset-top, 0px))",
                }}>
                  <Box
                    component="button"
                    onClick={closeJob}
                    aria-label={intl.formatMessage({ id: "candidate.job_detail.back_to_results" })}
                    sx={{
                      width: "44px", height: "44px", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: "none", bgcolor: "transparent", cursor: "pointer",
                      borderRadius: "12px", color: "hsl(178, 93%, 10%)",
                      "&:active": { bgcolor: "hsla(160, 10%, 96%, 0.8)" },
                    }}
                  >
                    <ArrowLeft size={22} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{
                      fontSize: "14px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                      color: "hsl(178, 93%, 10%)",
                    }}>
                      {job.job_title}
                    </Typography>
                    <Typography noWrap sx={{
                      fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      color: "hsl(178, 10%, 45%)",
                    }}>
                      {job.company?.name}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1, overflowY: "auto", p: "16px" }}>
                  <JobDetailPanel
                    jobOffer={job}
                    updateJobOffer={updateJobOffer}
                    onNavigate={navigateAway}
                  />
                </Box>
              </>
            );
          })()}
        </Dialog>
      )}
    </Box>
  );
};

export default JobsTab;
